<template>
  <RevCard class="relative flex gap-16 p-16" :hoverable="true">
    <NuxtLink
      :aria-label="i18n(translations.seeProduct, { title: product.title })"
      class="absolute inset-0 rounded-lg"
      :target="isDesktop ? '_blank' : undefined"
      :to="toNuxtLink(product.productPageLink)"
      @click="emit('product-click')"
    />

    <img
      :alt="product.title"
      class="pointer-events-none size-72 rounded-lg object-cover"
      :src="product.image"
    />

    <div class="pointer-events-none flex flex-1 flex-col justify-between gap-8">
      <div>
        <RevTag
          v-if="dealTag"
          class="mb-8"
          :icon="dealTag.icon"
          :label="dealTag.label"
          size="small"
          variant="success"
          variation="filled"
        />
        <p class="body-1-bold text-static-default-hi">{{ product.title }}</p>
        <p class="label-medium text-static-default-low">
          {{ i18n(translations.condition, { grade: product.grade.name }) }}
        </p>
      </div>

      <ProductPrice
        :deal="eligibleDeal"
        :new-price="product.priceNew"
        :price="product.price"
      />

      <div class="flex flex-wrap items-center gap-8">
        <RevChip
          class="pointer-events-auto relative"
          :label="formattedTimestamp"
          size="medium"
          :start-icon="IconMediaPlay"
          @click="emit('jump-to-moment')"
        />

        <!-- Mirrors nuxt-layer-recommendation's `AddToCartButton` compact
        format (icon swap for pending/success/error, same primary variant) —
        see useLiveShoppingAddToCart.ts for why it's reimplemented rather
        than reusing that component directly. -->
        <RevButtonIcon
          :alternative-text="i18n(translations.addToCart)"
          class="pointer-events-auto relative ml-auto"
          :icon="addToCartIcon"
          :loading="status === 'pending'"
          variant="primary"
          @click.stop="handleAddToCart"
        />
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import ProductPrice from '@backmarket/nuxt-layer-recommendation/ProductPrice.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevChip } from '@ds/components/Chip'
import { RevTag } from '@ds/components/Tag'
import { IconAddToCart } from '@ds/icons/IconAddToCart'
import { IconArrowDownRightInCircleFilled } from '@ds/icons/IconArrowDownRightInCircleFilled'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'
import { IconMediaPlay } from '@ds/icons/IconMediaPlay'
import { IconWarning } from '@ds/icons/IconWarning'
import { useMediaQuery } from '@vueuse/core'

import { NuxtLink } from '#components'

import { useLiveShoppingAddToCart } from '../composables/useLiveShoppingAddToCart'
import type { LiveShoppingProduct } from '../composables/useLiveShoppingProducts'

import translations from './LiveShoppingProductCard.translations'

const { product } = defineProps<{
  product: LiveShoppingProduct
}>()

const emit = defineEmits<{
  (e: 'jump-to-moment'): void
  (e: 'product-click'): void
  (e: 'add-to-cart'): void
}>()

const i18n = useI18n()

// Mirrors the `md` Tailwind breakpoint — PDP links open in a new tab on
// desktop so leaving the widget doesn't lose the live video; on mobile
// there's no benefit to a new tab (no persistent widget to come back to).
// `ssrWidth` keeps `useMediaQuery` itself SSR-safe (this card is only ever
// rendered behind the widget's `<ClientOnly>` boundary, but this doesn't
// rely on that call-site detail).
const isDesktop = useMediaQuery('(min-width: 768px)', { ssrWidth: 390 })

const { addToCart, status } = useLiveShoppingAddToCart()

const formattedTimestamp = computed(() => {
  const minutes = Math.floor(product.timestampSeconds / 60)
  const seconds = product.timestampSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

const addToCartIcon = computed(() => {
  if (status.value === 'success') return IconCheckSmall
  if (status.value === 'error') return IconWarning

  return IconAddToCart
})

// Guards via a separate computed rather than narrowing `status.value`
// directly — narrowing it here would otherwise persist across the `await`
// below (TS can't see that `addToCart` mutates it) and make the post-await
// success check unreachable by its type.
const isAddingOrAdded = computed(
  () => status.value === 'pending' || status.value === 'success',
)

// Mirrors AddToCartButton.vue's own behavior — tracked only on a real
// success, not on click intent or on error.
async function handleAddToCart() {
  if (isAddingOrAdded.value) return
  await addToCart(product)
  if (status.value === 'success') emit('add-to-cart')
}

// Deals are meaningful at the specific listing/backbox level; our curated
// products are fetched at parent/model granularity (see
// useLiveShoppingProducts.ts) for catalog stability, so this doesn't gate on
// granularity like useProductCardDeal.ts does elsewhere in the app — it just
// surfaces whatever deal the curated objectID's hit carries, if any.
const eligibleDeal = computed(() =>
  product.deals?.find(
    (deal) => deal.type === 'flash_sales' || deal.type === 'seller_flash_sales',
  ),
)

const dealTag = computed(() => {
  if (eligibleDeal.value?.type === 'flash_sales') {
    return { label: i18n(translations.flashSalesTag), icon: IconBoltFilled }
  }

  if (eligibleDeal.value?.type === 'seller_flash_sales') {
    return {
      label: i18n(translations.sellerFlashSalesTag),
      icon: IconArrowDownRightInCircleFilled,
    }
  }

  return undefined
})
</script>
